import React, { useState } from 'react';
import Lightbox from './Lightbox';
import OptimizedImage from './OptimizedImage';

/**
 * Portfolio grid component with filtering and lightbox
 * Optimized version with better image loading performance
 * 
 * @param {Object} props - Component props
 * @param {Array} props.items - Array of portfolio items
 * @returns {React.ReactElement} PortfolioGrid component
 */
const PortfolioGridOptimized = ({ items }) => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedItem, setSelectedItem] = useState(null);

  // Extract unique categories for filter pills
  // Define coming soon categories
  const comingSoonCategories = ['mapping', 'inspections'];
  
  // Extract unique categories from items (handle combined categories like "real-estate videography")
  const allCategories = Array.from(new Set(items.map(item => {
    // For combined categories like "real-estate videography", use the first part
    return item.category.split(' ')[0];
  })));
  
  // Separate active and coming soon categories
  const activeCategories = allCategories.filter(cat => !comingSoonCategories.includes(cat));
  const inactiveCategories = allCategories.filter(cat => comingSoonCategories.includes(cat));
  
  // Create the final categories array with active first, then coming soon
  const categories = [
    { id: 'all', label: 'All', isComingSoon: false },
    ...activeCategories.map(category => ({
      id: category,
      label: category.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
      isComingSoon: false
    })),
    ...inactiveCategories.map(category => ({
      id: category,
      label: category.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
      isComingSoon: true
    }))
  ];

  // Filter items based on active filter
  const filteredItems = activeFilter === 'all'
    ? items.filter(item => !comingSoonCategories.includes(item.category.split(' ')[0])) // Exclude coming soon categories from All
    : items.filter(item => {
        // Handle combined categories (e.g., "real-estate videography")
        if (activeFilter === 'videography') {
          return item.category.includes('videography');
        } else {
          return item.category.split(' ')[0] === activeFilter;
        }
      });

  // Open lightbox with selected item
  const openLightbox = (item) => {
    setSelectedItem(item);
  };

  // Close lightbox
  const closeLightbox = () => {
    setSelectedItem(null);
  };

  return (
    <>
      {/* Filter Pills */}
      <div className="flex flex-wrap justify-center gap-2 mb-8">
        {categories.map(category => (
          <button
            key={category.id}
            className={`px-4 py-2 rounded-full transition-colors relative ${
              activeFilter === category.id
                ? category.isComingSoon
                  ? 'bg-brand-blue-600/40 text-white/60' // Dimmed active coming soon
                  : 'bg-brand-blue-600 text-white' // Normal active
                : category.isComingSoon
                  ? 'bg-gray-700/40 text-white/60 hover:bg-gray-600/40 cursor-default' // Dimmed inactive coming soon
                  : 'bg-gray-700 text-white hover:bg-gray-600' // Normal inactive
            }`}
            onClick={() => !category.isComingSoon && setActiveFilter(category.id)}
            disabled={category.isComingSoon}
          >
            {category.label}
            {category.isComingSoon && (
              <span className="absolute -top-1 -right-1 bg-brand-blue-600/80 text-white text-xs px-1 rounded-full">Soon</span>
            )}
          </button>
        ))}
      </div>

      {/* Group items by type */}
      {(() => {
        // Group items by type (photo/video)
        const photoItems = filteredItems.filter(item => item.type === 'photo' || !item.type);
        const videoItems = filteredItems.filter(item => item.type === 'video');
        
        return (
          <>
            {/* Photos Section */}
            {photoItems.length > 0 && (
              <div className="mb-12">
                <h2 className="text-2xl font-bold mb-6 text-white">Photography</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {photoItems.map(item => (
                    <div 
                      key={item.id} 
                      className="rounded-2xl overflow-hidden shadow-card cursor-pointer group"
                      onClick={() => openLightbox(item)}
                    >
                      <div className="relative aspect-[3/2] overflow-hidden">
                        <OptimizedImage 
                          src={item.thumb} 
                          alt={item.title} 
                          className="w-full h-full transition-transform duration-500 group-hover:scale-105"
                          sizes={{
                            default: '100vw',
                            breakpoints: [
                              { media: '(min-width: 1280px)', size: '33vw' },
                              { media: '(min-width: 768px)', size: '50vw' },
                            ]
                          }}
                        />
                        <div className="absolute inset-0 bg-brand-navy/0 group-hover:bg-brand-navy/30 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                          <span className="text-white font-medium px-4 py-2 rounded-lg bg-brand-navy/70">
                            {item.hasCarousel ? 'View Gallery' : 'View Details'}
                          </span>
                        </div>
                        {item.hasCarousel && (
                          <div className="absolute top-2 right-2 bg-brand-blue-600 rounded-full p-2">
                            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                            </svg>
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <h3 className="font-bold text-white">{item.title}</h3>
                        <p className="text-sm text-gray-300 capitalize">
                          {item.category.split(' ')[0].replace('-', ' ')}
                        </p>
                        {item.location && (
                          <p className="text-sm text-brand-blue-600 mt-1">
                            {item.location}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Videos Section */}
            {videoItems.length > 0 && (
              <div>
                <h2 className="text-2xl font-bold mb-6 text-white">Videography</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {videoItems.map(item => (
                    <div 
                      key={item.id} 
                      className="rounded-2xl overflow-hidden shadow-card cursor-pointer group"
                      onClick={() => openLightbox(item)}
                    >
                      <div className="relative aspect-[3/2] overflow-hidden">
                        <OptimizedImage 
                          src={item.thumb} 
                          alt={item.title} 
                          className="w-full h-full transition-transform duration-500 group-hover:scale-105"
                          sizes={{
                            default: '100vw',
                            breakpoints: [
                              { media: '(min-width: 1280px)', size: '33vw' },
                              { media: '(min-width: 768px)', size: '50vw' },
                            ]
                          }}
                        />
                        <div className="absolute inset-0 bg-brand-navy/0 group-hover:bg-brand-navy/30 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                          <span className="text-white font-medium px-4 py-2 rounded-lg bg-brand-navy/70">
                            View Details
                          </span>
                        </div>
                        {/* Video indicator */}
                        <div className="absolute top-2 right-2 bg-brand-blue-600 rounded-full p-2">
                          <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                          </svg>
                        </div>
                      </div>
                      <div className="p-4">
                        <h3 className="font-bold text-white">{item.title}</h3>
                        <p className="text-sm text-gray-300 capitalize">
                          {item.category.split(' ')[0].replace('-', ' ')}
                        </p>
                        {item.location && (
                          <p className="text-sm text-brand-blue-600 mt-1">
                            {item.location}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        );
      })()}

      {/* Lightbox */}
      {selectedItem && (
        <Lightbox item={selectedItem} onClose={closeLightbox} />
      )}
    </>
  );
};

export default PortfolioGridOptimized;
